import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { loginUser, verifyTotp, clearMessage } from "../../../_actions/user_actions";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Button,
  Checkbox,
  Typography,
  Modal,
  message,
} from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";

const { Title } = Typography;

/**
 * Sys‑Admin login page – handles both password and TOTP steps without extra routes.
 * Flow:
 *   1. Submit email + password → loginUser.
 *   2. If backend returns 202 (TOTP_REQUIRED) action puts tmp_token into Redux.
 *   3. Component detects `auth.pendingTotp` → opens modal.
 *   4. Submit 6‑digit code → verifyTotp(tmp_token, code).
 *   5. On success LOGIN_USER fires again, modal closes, redirect to /sysadmin.
 */
function LoginPage(props) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.user); // assumes user reducer
  console.log(props)
  const newMessage = props?.data?.user?.message ?? null

  const rememberDefault = !!localStorage.getItem("rememberMe");
  const [rememberMe, setRememberMe] = useState(rememberDefault);
  const [formError, setFormError] = useState("");
  const [code, setCode] = useState("");

  // When LOGIN_USER lands (authenticated) close modal + redirect
  useEffect(() => {
    const finish = () => props.history.replace("/sysadmin/schools");
    if (auth.isAuth && !auth.pendingTotp) finish();
  }, [auth.isAuth, auth.pendingTotp, props.history]);

  useEffect(() => {
    if (newMessage) {
      message.error(newMessage);
      dispatch(clearMessage());
    }
  }, [newMessage, dispatch]);

  return (
    <Formik
      initialValues={{
        email: localStorage.getItem("rememberMe") || "",
        password: "",
      }}
      validationSchema={Yup.object({
        email: Yup.string().email("Invalid email").required("Required"),
        password: Yup.string().min(6).required("Required"),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await dispatch(
            loginUser({
              user: values.email.toLowerCase(),
              passwd: values.password,
            })
          );
          // if no TOTP required the redirect will happen via useEffect
          rememberMe
            ? localStorage.setItem("rememberMe", values.email)
            : localStorage.removeItem("rememberMe");
        } catch (e) {
          setFormError("Invalid username or password");
          setTimeout(() => setFormError(""), 3000);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
      }) => (
        <div className="app">
          <Title level={2}>Sys‑Admin Login</Title>
          <form onSubmit={handleSubmit} style={{ width: 350 }}>
            <Form.Item>
              <Input
                id="email"
                prefix={<UserOutlined />}
                placeholder="Email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.email && touched.email && (
                <div className="input-feedback">{errors.email}</div>
              )}
            </Form.Item>

            <Form.Item>
              <Input.Password
                id="password"
                prefix={<LockOutlined />}
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.password && touched.password && (
                <div className="input-feedback">{errors.password}</div>
              )}
            </Form.Item>

            {formError && <p style={{ color: "red" }}>{formError}</p>}

            <Form.Item>
              <Checkbox
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              >
                Remember me
              </Checkbox>
              <Button
                type="primary"
                htmlType="submit"
                block
                style={{ marginTop: 12 }}
                disabled={isSubmitting}
              >
                Log in
              </Button>
            </Form.Item>
          </form>

          {/* ── TOTP modal ─────────────────────────────────────────── */}
          {auth.pendingTotp && (
            <Modal
              open
              title="Two‑Factor Authentication"
              footer={null}
              closable={false}
            >
              <Input
                autoFocus
                placeholder="6‑digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />

              <Button
                type="primary"
                block
                style={{ marginTop: 12 }}
                onClick={async () => {
                  if (!/^[0-9]{6}$/.test(code)) {
                    return message.error("Enter 6 digits");
                  }
                  try {
                    await dispatch(verifyTotp(auth.pendingTotp, code));
                  } catch {
                    message.error("Invalid code");
                  }
                }}
              >
                Verify
              </Button>
            </Modal>
          )}
        </div>
      )}
    </Formik>
  );
}

function mapStateToProps(state) {
  return {
    data: state
  };
}

export default withRouter(connect(mapStateToProps)(LoginPage))
